/**
 * Centralized Auth Hook
 *
 * Manages the complete auth lifecycle:
 * 1. GoTrue session on main project
 * 2. Token enhancement (prototype claims)
 * 3. Token exchange (local project access)
 *
 * @see docs/DUAL-CLIENT-SETUP.md
 * @see docs/MULTI-TENANCY-AUTH.md
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { User, AuthError, Session } from '@supabase/supabase-js';
import {
  mainSupabase,
  localSupabase,
  enhanceMainToken,
  exchangeLocalToken,
  exchangeCrmToken,
  exchangeCrmTokenFromLocal,
  setMainToken,
  setLocalToken,
  setLocalAccessToken,
  setMainAccessToken,
  setCrmAccessToken,
  isSupabaseConfigured,
  getCachedLocalToken,
  setCachedLocalToken,
  clearCachedLocalToken,
  getCachedCrmToken,
  setCachedCrmToken,
  clearCachedCrmToken,
  getLocalAccessToken,
  getCrmAccessToken,
  isJwtNearExpiry,
  registerDownstreamRefresher,
  type EnhanceTokenResult,
} from '@/lib/supabase-clients';
import { resetSharedFilterScope, setSharedFilterScope, clearAllSharedFilterStorage } from '@/lib/shared-client-filter';

// ============================================================================
// STATE INTERFACE
// ============================================================================

interface AuthState {
  user: User | null;
  mainToken: string | null;
  enhancedMainToken: string | null;
  localToken: string | null;
  crmToken: string | null;
  crmTokenReady: boolean;
  mainTokenReady: boolean;
  prototypeInfo: {
    id: string;
    name: string;
    is_internal: boolean;
  } | null;
  authSource: 'main' | 'local' | null;
  loading: boolean;
  error: AuthError | Error | null;
  isConfigured: boolean;
}

const initialState: AuthState = {
  user: null,
  mainToken: null,
  enhancedMainToken: null,
  localToken: null,
  crmToken: null,
  crmTokenReady: false,
  mainTokenReady: false,
  prototypeInfo: null,
  authSource: null,
  loading: true,
  error: null,
  isConfigured: isSupabaseConfigured(),
};

const PERSISTED_AUTH_KEYS = ['main-auth-token', 'local-auth-token'] as const;

// ============================================================================
// Sign-out coordination
// ============================================================================
//
// `signOut` MUST be a redirect-only operation. Doing any token / storage /
// React-state teardown in the currently-mounted (authed) tree triggers a
// cascade: GoTrue storage events → onAuthStateChange → setState({user:null})
// → every provider re-renders (OwnerFilterContext re-seeds, React Query
// refetches without tokens, ProtectedRoute redirects) → main thread freezes
// before window.location.replace gets a chance to unload the page.
//
// Instead we stamp a flag in sessionStorage and navigate immediately. The
// `/auth` page (rendered in an unauthenticated tree with no providers that
// could thrash) consumes the flag and performs the real cleanup on mount.

const PENDING_SIGNOUT_KEY = 'auth:pending-signout';
let isSigningOut = (() => {
  try { return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(PENDING_SIGNOUT_KEY) === '1'; }
  catch { return false; }
})();

export function isSignOutInProgress(): boolean {
  if (isSigningOut) return true;
  try {
    return sessionStorage.getItem(PENDING_SIGNOUT_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Performs the real sign-out cleanup. Safe to call from the `/auth` page on
 * mount when a pending sign-out flag is detected. Never call from inside an
 * authenticated provider tree — that's the freeze surface this whole pattern
 * exists to avoid.
 */
export async function consumePendingSignOut(): Promise<void> {
  try { sessionStorage.removeItem(PENDING_SIGNOUT_KEY); } catch { /* ignore */ }
  isSigningOut = false;

  clearAllSharedFilterStorage();
  resetSharedFilterScope();
  setLocalAccessToken(null);
  setMainAccessToken(null);
  setCrmAccessToken(null);
  clearCachedLocalToken();
  clearCachedCrmToken();
  clearPersistedAuthStorage();

  // Fire-and-forget; the server-side revocation continues in the background.
  try { mainSupabase.auth.signOut({ scope: 'global' }).catch(() => {}); } catch { /* ignore */ }
  try { localSupabase.auth.signOut({ scope: 'local' }).catch(() => {}); } catch { /* ignore */ }
}


function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return '';
}

function isInvalidPersistedTokenError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return message.includes('Invalid user token') || message.includes('Invalid main token');
}

function clearPersistedAuthStorage() {
  try {
    PERSISTED_AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
    // Sweep any GoTrue/Supabase artifacts written under different key shapes
    // across SDK versions (sb-*, *-auth-token, supabase.*).
    Object.keys(localStorage).forEach((k) => {
      if (/auth-token|supabase|sb-/i.test(k)) {
        try { localStorage.removeItem(k); } catch { /* ignore */ }
      }
    });
  } catch {
    // Ignore storage access failures.
  }
  try {
    Object.keys(sessionStorage).forEach((k) => {
      if (/auth-token|supabase|sb-|jwt-cache/i.test(k)) {
        try { sessionStorage.removeItem(k); } catch { /* ignore */ }
      }
    });
  } catch {
    // Ignore storage access failures.
  }
}

// ============================================================================
// HOOK
// ============================================================================

export function useCentralizedAuth() {
  const [state, setState] = useState<AuthState>(initialState);

  const resetInvalidPersistedSession = useCallback(async () => {
    clearAllSharedFilterStorage();
    resetSharedFilterScope();
    setLocalAccessToken(null);
    setMainAccessToken(null);
    setCrmAccessToken(null);
    clearCachedLocalToken();
    clearCachedCrmToken();
    clearPersistedAuthStorage();
    setupTokenRef.current = null;

    await Promise.allSettled([
      localSupabase.auth.signOut(),
      mainSupabase.auth.signOut(),
    ]);

    setState((s) => ({
      ...s,
      user: null,
      mainToken: null,
      enhancedMainToken: null,
      localToken: null,
      crmToken: null,
      crmTokenReady: false,
      mainTokenReady: false,
      prototypeInfo: null,
      authSource: null,
      loading: false,
      error: null,
    }));
  }, []);

  // --------------------------------------------------------------------------
  // Token Setup (non-blocking background task)
  // --------------------------------------------------------------------------

  // Dedupe: avoid invoking edge functions multiple times for the same token
  // (StrictMode + onAuthStateChange + getSession can otherwise trigger 3-4x).
  const setupTokenRef = useRef<string | null>(null);

  // Retry helper: only retries on transient errors (network/5xx).
  // Auth errors ("Invalid user token" / "Invalid main token") are NOT retried —
  // they bubble up so the caller can clear the persisted session.
  async function retryTransient<T>(
    fn: () => Promise<T>,
    label: string,
    attempts = 3,
  ): Promise<T> {
    let lastErr: unknown;
    for (let i = 0; i < attempts; i++) {
      try {
        return await fn();
      } catch (err) {
        lastErr = err;
        if (isInvalidPersistedTokenError(err)) throw err; // hard auth failure
        const msg = getErrorMessage(err);
        const transient =
          msg.includes('Failed to fetch') ||
          msg.includes('NetworkError') ||
          msg.includes('timeout') ||
          /\b5\d\d\b/.test(msg);
        if (!transient || i === attempts - 1) throw err;
        const delay = 250 * 2 ** i;
        console.warn(`[Auth] ${label} attempt ${i + 1} failed, retrying in ${delay}ms:`, msg);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
    throw lastErr;
  }

  const setupTokens = useCallback(async (mainTokenInput: string, userId: string, force = false) => {
    if (!force && setupTokenRef.current === mainTokenInput) return;
    setupTokenRef.current = mainTokenInput;

    let mainToken = mainTokenInput;

    // Step 1: Ensure token is fresh
    try {
      const payload = JSON.parse(atob(mainToken.split('.')[1]));
      const nowSec = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp - nowSec < 60) {
        const { data: { session } } = await mainSupabase.auth.refreshSession();
        mainToken = session?.access_token || mainToken;
        setupTokenRef.current = mainToken;
      }
    } catch {
      // ignore decode/refresh failures, continue with original token
    }

    // Step 2: Exchange — skip the edge function entirely on warm loads.
    const cached = getCachedLocalToken(userId);
    const exchangePromise: Promise<{ access_token: string; expires_at?: number } | null> = cached
      ? Promise.resolve({ access_token: cached.token, expires_at: cached.exp })
      : retryTransient(() => exchangeLocalToken(mainToken), 'exchange-token').then((r) => {
          setCachedLocalToken(userId, r.access_token, r.expires_at);
          return r;
        });

    // Step 2b: CRM exchange — same warm-load fast path as local.
    const cachedCrm = getCachedCrmToken(userId);
    const crmExchangePromise: Promise<{ access_token: string; expires_at?: number } | null> = cachedCrm
      ? Promise.resolve({ access_token: cachedCrm.token, expires_at: cachedCrm.exp })
      : retryTransient(() => exchangeCrmToken(mainToken), 'exchange-crm-token').then((r) => {
          setCachedCrmToken(userId, r.access_token, r.expires_at);
          return r;
        });

    // Step 3: Enhance runs in parallel with both exchanges.
    const [enhanceRes, exchangeRes, crmExchangeRes] = await Promise.allSettled([
      retryTransient(() => enhanceMainToken(mainToken), 'enhance-token'),
      exchangePromise,
      crmExchangePromise,
    ]);

    const enhanceError = enhanceRes.status === 'rejected' ? enhanceRes.reason : null;
    const exchangeError = exchangeRes.status === 'rejected' ? exchangeRes.reason : null;

    // -- Enhance branch -------------------------------------------------------
    if (enhanceRes.status === 'fulfilled') {
      const enhanceResult: EnhanceTokenResult = enhanceRes.value;
      setMainToken(enhanceResult.access_token);
      setMainAccessToken(enhanceResult.access_token);
      setState(s => ({
        ...s,
        enhancedMainToken: enhanceResult.access_token,
        mainTokenReady: true,
        prototypeInfo: enhanceResult.prototype,
      }));
    } else {
      // DEGRADE (do NOT sign out): keep the raw GoTrue token as _mainAccessToken
      // so RLS-permissive Core SELECTs still work. Writes that need
      // `pg_can_write()` will fail explicitly because mainTokenReady=false.
      console.warn(
        '[Auth] enhance-token failed (degrading to raw main token):',
        getErrorMessage(enhanceError) || enhanceError,
      );
      setMainToken(mainToken);
      setMainAccessToken(mainToken);
      setState(s => ({
        ...s,
        enhancedMainToken: null,
        mainTokenReady: false,
        prototypeInfo: null,
      }));
    }

    // -- Exchange branch ------------------------------------------------------
    let localOk = false;
    if (exchangeRes.status === 'fulfilled' && exchangeRes.value) {
      setLocalToken(exchangeRes.value.access_token);
      setLocalAccessToken(exchangeRes.value.access_token);
      setState(s => ({ ...s, localToken: exchangeRes.value!.access_token }));
      localOk = true;
    } else {
      // Preserve any in-memory / cached token if we already had one.
      const stillCached = getCachedLocalToken(userId);
      if (stillCached) {
        setLocalAccessToken(stillCached.token);
        setLocalToken(stillCached.token);
        setState(s => ({ ...s, localToken: stillCached.token }));
        localOk = true;
        console.warn(
          '[Auth] exchange-token failed, kept cached local JWT:',
          getErrorMessage(exchangeError) || exchangeError,
        );
      } else {
        console.warn(
          '[Auth] exchange-token failed and no cached local JWT available:',
          getErrorMessage(exchangeError) || exchangeError,
        );
      }
    }

    // -- CRM exchange branch --------------------------------------------------
    const crmExchangeError = crmExchangeRes.status === 'rejected' ? crmExchangeRes.reason : null;
    if (crmExchangeRes.status === 'fulfilled' && crmExchangeRes.value) {
      setCrmAccessToken(crmExchangeRes.value.access_token);
      setState(s => ({
        ...s,
        crmToken: crmExchangeRes.value!.access_token,
        crmTokenReady: true,
      }));
    } else {
      const stillCachedCrm = getCachedCrmToken(userId);
      if (stillCachedCrm) {
        setCrmAccessToken(stillCachedCrm.token);
        setState(s => ({ ...s, crmToken: stillCachedCrm.token, crmTokenReady: true }));
        console.warn(
          '[Auth] exchange-crm-token failed, kept cached CRM JWT:',
          getErrorMessage(crmExchangeError) || crmExchangeError,
        );
      } else {
        console.warn(
          '[Auth] exchange-crm-token failed and no cached CRM JWT available:',
          getErrorMessage(crmExchangeError) || crmExchangeError,
        );
        setState(s => ({ ...s, crmToken: null, crmTokenReady: false }));
      }
    }

    // -- Session-fatal: ONLY when BOTH paths are dead -------------------------
    // Enhance hard-failed AND we have no usable local token AND the exchange
    // also hard-failed with an auth error. In that state nothing the user
    // could do in-app would work — bounce to /auth (once) with a reason.
    const enhanceHardFail = isInvalidPersistedTokenError(enhanceError);
    const exchangeHardFail = isInvalidPersistedTokenError(exchangeError);
    if (!localOk && enhanceHardFail && exchangeHardFail) {
      console.warn('[Auth] Both enhance and exchange failed with auth errors — forcing re-login.');
      await resetInvalidPersistedSession();
      try {
        const alreadyRedirected = sessionStorage.getItem('auth-redirected-once');
        if (window.location.pathname !== '/auth' && !alreadyRedirected) {
          sessionStorage.setItem('auth-redirected-once', '1');
          window.location.replace('/auth?reason=session_expired');
        }
      } catch {
        /* ignore storage errors */
      }
      return;
    }
  }, [resetInvalidPersistedSession]);


  // --------------------------------------------------------------------------
  // Session Handler
  // --------------------------------------------------------------------------

  const handleSession = useCallback(async (session: Session | null) => {
    // Sign-out in progress: do nothing. The page is unloading; any state
    // teardown here just re-renders the authed tree against user=null and
    // freezes the main thread before navigation completes.
    if (isSignOutInProgress()) return;
    if (session?.user) {
      setSharedFilterScope(session.user.id);
      // Hydrate the local token from sessionStorage SYNCHRONOUSLY when we have
      // a fresh cache entry for this user. This is the warm-load fast path:
      // queries that depend on `localToken` (roles, ACL, channel fetches) can
      // fire in the same tick instead of waiting on the exchange-token edge
      // function. RLS surface is identical — same JWT, same `sub`.
      const cached = getCachedLocalToken(session.user.id);
      if (cached) {
        setLocalAccessToken(cached.token);
        setLocalToken(cached.token);
      }
      const cachedCrm = getCachedCrmToken(session.user.id);
      if (cachedCrm) {
        setCrmAccessToken(cachedCrm.token);
      }

      // Set user immediately so UI can render
      setState(s => ({
        ...s,
        user: session.user,
        mainToken: session.access_token,
        localToken: cached ? cached.token : s.localToken,
        crmToken: cachedCrm ? cachedCrm.token : s.crmToken,
        crmTokenReady: cachedCrm ? true : s.crmTokenReady,
        authSource: 'main',
        loading: false,
        error: null,
      }));

      // Token setup is non-blocking. On cache hit it skips the exchange call;
      // on miss it performs a live exchange and populates the cache.
      setupTokens(session.access_token, session.user.id).catch(err => {
        console.warn('[Auth] Background token setup error (non-fatal):', err);
      });
    } else {
      // Clear all state
      resetSharedFilterScope();
      setLocalAccessToken(null);
      setMainAccessToken(null);
      setCrmAccessToken(null);
      clearPersistedAuthStorage();
      clearCachedLocalToken();
      clearCachedCrmToken();
      setupTokenRef.current = null;
      setState(s => ({
        ...s,
        user: null,
        mainToken: null,
        enhancedMainToken: null,
        localToken: null,
        crmToken: null,
        crmTokenReady: false,
        mainTokenReady: false,
        prototypeInfo: null,
        authSource: null,
        loading: false,
        error: null,
      }));
    }
  }, [setupTokens]);

  // --------------------------------------------------------------------------
  // Local-only Session Handler (fallback for users that exist only in Local)
  // --------------------------------------------------------------------------

  const handleLocalSession = useCallback((session: Session | null) => {
    if (isSignOutInProgress()) return;
    if (!session?.user) return;
    // Local-only is a STRICT FALLBACK. It must never demote an existing
    // main session, and it must never run while a main session is stored.
    const hasMainStored = !!localStorage.getItem('main-auth-token');
    if (hasMainStored) {
      localSupabase.auth.signOut().catch(() => {});
      return;
    }

    setSharedFilterScope(session.user.id);

    // Warm CRM token from cache so `crmTokenReady` flips true synchronously
    // when we have a fresh entry for this local-only user.
    const cachedCrm = getCachedCrmToken(session.user.id);
    if (cachedCrm) setCrmAccessToken(cachedCrm.token);

    setState(s => {
      if (s.user && s.authSource === 'main') {
        // Main already adopted — purge the local SDK session and bail.
        localSupabase.auth.signOut().catch(() => {});
        return s;
      }
      setLocalAccessToken(session.access_token);
      return {
        ...s,
        user: session.user,
        mainToken: null,
        enhancedMainToken: null,
        localToken: session.access_token,
        crmToken: cachedCrm ? cachedCrm.token : s.crmToken,
        crmTokenReady: cachedCrm ? true : s.crmTokenReady,
        mainTokenReady: false,
        prototypeInfo: null,
        authSource: 'local',
        loading: false,
        error: null,
      };
    });

    // Local-only users have no main token, so CRM's exchange-token won't
    // accept them. Mint a CRM session via our local proxy instead.
    if (!cachedCrm) {
      exchangeCrmTokenFromLocal(session.access_token)
        .then((r) => {
          setCrmAccessToken(r.access_token);
          setCachedCrmToken(session.user.id, r.access_token, r.expires_at);
          setState(s => ({ ...s, crmToken: r.access_token, crmTokenReady: true }));
        })
        .catch((err) => {
          console.warn('[Auth] mint-crm-token-local failed (non-fatal):', err);
        });
    }
  }, []);

  // --------------------------------------------------------------------------
  // Auth Listener + Initial Session Check
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (!state.isConfigured) {
      setState(s => ({ ...s, loading: false }));
      return;
    }

    // Listen for auth state changes FIRST (per Supabase best practices).
    // We ONLY listen on the main project. The local project's GoTrue is
    // never the source of truth for dual-tenant users — its token is the
    // exchanged JWT held in supabase-clients._localAccessToken. Listening
    // on local caused races that demoted super_admins to authSource:'local'
    // and wiped enhancedMainToken mid-session.
    const { data: { subscription } } = mainSupabase.auth.onAuthStateChange(
      (_event, session) => {
        // Use setTimeout to prevent Supabase auth deadlock
        setTimeout(() => handleSession(session), 0);
      }
    );

    // Then check existing main session
    mainSupabase.auth.getSession().then(({ data: { session } }) => {
      handleSession(session);
      // Local-only fallback only matters when there is no main session AND
      // no main session was ever stored. This avoids the local-first race.
      if (!session && !localStorage.getItem('main-auth-token')) {
        localSupabase.auth.getSession().then(({ data: { session: localSession } }) => {
          if (localSession) handleLocalSession(localSession);
        });
      }
    });

    // ------------------------------------------------------------------------
    // Long-term hang prevention: proactively keep the downstream (Local + CRM)
    // JWTs fresh. Without this, the Local JWT silently expires while the main
    // token is still valid, every PostgREST call returns 401, React Query
    // retries forever, and the app gets stuck on the loading spinner.
    // ------------------------------------------------------------------------
    // Refresh downstream tokens for a LOCAL-ONLY user (no main session).
    // GoTrue holds the refresh token in memory (persistSession:false), so
    // refreshSession() still works within the tab lifetime. After refresh,
    // re-mint the CRM JWT via mint-crm-token-local.
    const refreshLocalOnlyDownstream = async (): Promise<boolean> => {
      try {
        const { data: { session: localSession } } = await localSupabase.auth.getSession();
        if (!localSession?.user) return false;
        const { data: refreshed, error: refreshErr } = await localSupabase.auth.refreshSession();
        const newLocalSession = refreshed?.session ?? localSession;
        if (refreshErr || !newLocalSession?.access_token) {
          console.warn('[Auth] local-only refresh failed:', refreshErr);
          return false;
        }
        setLocalAccessToken(newLocalSession.access_token);
        setState(s => ({ ...s, localToken: newLocalSession.access_token }));
        try {
          const r = await exchangeCrmTokenFromLocal(newLocalSession.access_token);
          setCrmAccessToken(r.access_token);
          setCachedCrmToken(newLocalSession.user.id, r.access_token, r.expires_at);
          setState(s => ({ ...s, crmToken: r.access_token, crmTokenReady: true }));
        } catch (err) {
          console.warn('[Auth] mint-crm-token-local (refresh) failed:', err);
          return false;
        }
        return true;
      } catch (err) {
        console.warn('[Auth] refreshLocalOnlyDownstream error:', err);
        return false;
      }
    };

    const refreshIfStale = async (force: boolean) => {
      if (isSignOutInProgress()) return;
      try {
        const { data: { session } } = await mainSupabase.auth.getSession();
        if (session?.user) {
          const localStale = isJwtNearExpiry(getLocalAccessToken(), 5 * 60);
          const crmStale = isJwtNearExpiry(getCrmAccessToken(), 5 * 60);
          if (force || localStale || crmStale) {
            await setupTokens(session.access_token, session.user.id, true);
          }
          return;
        }
        // Local-only fallback path
        const localStale = isJwtNearExpiry(getLocalAccessToken(), 5 * 60);
        const crmStale = isJwtNearExpiry(getCrmAccessToken(), 5 * 60);
        if (force || localStale || crmStale) {
          await refreshLocalOnlyDownstream();
        }
      } catch (err) {
        console.warn('[Auth] proactive refresh failed:', err);
      }
    };

    // Expose the same refresher to the fetch layer so a request that 401s
    // due to a stale JWT can be replayed once against a fresh token without
    // ever surfacing an error to the UI.
    registerDownstreamRefresher(async () => {
      const { data: { session } } = await mainSupabase.auth.getSession();
      if (session?.user) {
        await setupTokens(session.access_token, session.user.id, true);
        return;
      }
      await refreshLocalOnlyDownstream();
    });

    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshIfStale(false);
    };
    const onFocus = () => refreshIfStale(false);
    const onDownstreamExpired = () => refreshIfStale(true);

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    window.addEventListener('auth:downstream-token-expired', onDownstreamExpired);

    // Periodic safety net (covers tabs that stay foreground for hours).
    const interval = window.setInterval(() => refreshIfStale(false), 60 * 1000);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('auth:downstream-token-expired', onDownstreamExpired);
      window.clearInterval(interval);
      registerDownstreamRefresher(null);
    };
  }, [handleSession, handleLocalSession, state.isConfigured, setupTokens]);

  // --------------------------------------------------------------------------
  // Auth Actions
  // --------------------------------------------------------------------------

  const signIn = useCallback(async (email: string, password: string) => {
    setState(s => ({ ...s, loading: true, error: null }));
    resetSharedFilterScope();
    try { sessionStorage.removeItem('auth-redirected-once'); } catch { /* ignore */ }
    // 1) Try Core/Main first — existing pipeline takes over via onAuthStateChange
    const { error: mainErr } = await mainSupabase.auth.signInWithPassword({ email, password });
    if (!mainErr) {
      // Purge any stale local-only SDK session so its GoTrue token doesn't
      // clobber the exchanged token for dual-tenant users.
      localSupabase.auth.signOut().catch(() => {});
      return { error: null };
    }

    // Main failed — purge any stale main-side session so the local-only
    // fallback isn't suppressed by handleLocalSession's `hasMainStored` /
    // `authSource === 'main'` guards (which exist for the passive listener
    // path, not the explicit sign-in path).
    await mainSupabase.auth.signOut().catch(() => {});
    try { localStorage.removeItem('main-auth-token'); } catch { /* ignore */ }
    setMainAccessToken(null);
    setState(s => ({
      ...s,
      user: null,
      mainToken: null,
      enhancedMainToken: null,
      mainTokenReady: false,
      authSource: null,
    }));

    // 2) Fallback: try Local
    const { data: localData, error: localErr } = await localSupabase.auth.signInWithPassword({ email, password });
    if (localErr || !localData.session) {
      // Surface the original Core error to the user (more meaningful UX)
      setState(s => ({ ...s, loading: false, error: mainErr }));
      return { error: mainErr };
    }
    // Adopt the local session directly (bypassing handleLocalSession guards).
    const localSession = localData.session;
    setSharedFilterScope(localSession.user.id);
    setLocalAccessToken(localSession.access_token);
    const cachedCrm = getCachedCrmToken(localSession.user.id);
    if (cachedCrm) setCrmAccessToken(cachedCrm.token);
    setState(s => ({
      ...s,
      user: localSession.user,
      mainToken: null,
      enhancedMainToken: null,
      localToken: localSession.access_token,
      crmToken: cachedCrm ? cachedCrm.token : s.crmToken,
      crmTokenReady: cachedCrm ? true : s.crmTokenReady,
      mainTokenReady: false,
      prototypeInfo: null,
      authSource: 'local',
      loading: false,
      error: null,
    }));
    // Background: mint a CRM session for this local-only user.
    if (!cachedCrm) {
      exchangeCrmTokenFromLocal(localSession.access_token)
        .then((r) => {
          setCrmAccessToken(r.access_token);
          setCachedCrmToken(localSession.user.id, r.access_token, r.expires_at);
          setState(s => ({ ...s, crmToken: r.access_token, crmTokenReady: true }));
        })
        .catch((err) => {
          console.warn('[Auth] mint-crm-token-local failed (non-fatal):', err);
        });
    }
    return { error: null };
  }, []);

  const signUp = useCallback(async (email: string, password: string, metadata?: { full_name?: string }) => {
    setState(s => ({ ...s, loading: true, error: null }));
    const { error } = await mainSupabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) {
      setState(s => ({ ...s, loading: false, error }));
    }
    return { error };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const { error } = await mainSupabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    // REDIRECT-ONLY sign-out. Do NOT touch tokens, storage, React state, or
    // call GoTrue from inside the authed tree — that's the freeze surface.
    // The real cleanup runs on /auth mount via `consumePendingSignOut()`.
    isSigningOut = true;
    try { sessionStorage.setItem(PENDING_SIGNOUT_KEY, '1'); } catch { /* ignore */ }
    try { window.location.replace('/auth?signout=1'); } catch { /* ignore */ }
    return { error: null };
  }, []);


  const resetPassword = useCallback(async (email: string) => {
    const { error } = await mainSupabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  }, []);

  return {
    ...state,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resetPassword,
  };
}
